import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  COUVERTURE_REMATCH_LABELS,
  DISCREPANCY_SCOPE_LABELS,
  VALIDATION_TYPE_LABELS,
  hasAtLeastRole,
  type ClosingQueryInput,
  type DiscrepancyScope,
  type PropositionRematch,
} from '@kn/shared';
import { api } from '../api/client';
import { messageErreur } from '../api/error-message';
import { useAuth } from '../auth/auth-context';

function formaterNombre(valeur: string | number): string {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return String(valeur);
  return nombre.toLocaleString('fr-FR', { maximumFractionDigits: 3 });
}

function formaterHorodatage(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

/** Une proposition ne se confirme pas à l'aveugle : le bouton dit ce qu'il fait. */
function libelleAction(proposition: PropositionRematch): string {
  if (proposition.couverture === 'ABSENTE') return 'Aucun ODE à reprendre';
  if (!proposition.validableMaintenant) return "L'écart n'est pas revenu à zéro";
  return 'Confirmer la bascule en définitive';
}

/**
 * Clôture de fin d'année.
 *
 * Deux temps : le **récapitulatif** des validations temporaires, que KN
 * utilise pour passer les ODE annuels ; puis le **re-match**, qui rapproche
 * les ODE passés des temporaires et propose la bascule en définitif. Rien
 * n'est transformé automatiquement (règle 14).
 */
export function CloturePage(): ReactElement {
  const annee = new Date().getFullYear();
  const [du, setDu] = useState(`${annee}-01-01`);
  const [au, setAu] = useState(`${annee}-12-31`);
  const [maille, setMaille] = useState<DiscrepancyScope>('ARTICLE');
  const [erreur, setErreur] = useState<string | null>(null);
  const { user } = useAuth();
  const peutValider = user ? hasAtLeastRole(user.role, 'OPERATEUR') : false;
  const client = useQueryClient();

  const filtres: ClosingQueryInput = useMemo(() => ({ du, au, maille }), [du, au, maille]);

  const recap = useQuery({
    queryKey: ['cloture', 'recapitulatif', filtres],
    queryFn: () => api.recapitulatifTemporaires(filtres),
  });
  const rematch = useQuery({
    queryKey: ['cloture', 'rematch', filtres],
    queryFn: () => api.propositionsRematch(filtres),
  });
  const historique = useQuery({
    queryKey: ['cloture', 'historique', du, au],
    queryFn: () => api.historiqueValidations({ du, au }),
  });

  const basculer = useMutation({
    mutationFn: (proposition: PropositionRematch) =>
      api.validerEcart(proposition.discrepancyId, {
        type: 'DEFINITIVE',
        quantite: '0',
        commentaire: proposition.commentaire,
        ...(proposition.odes[0]?.numcmm ? { motif: 'ODE' } : {}),
      }),
    onSuccess: () => {
      setErreur(null);
      void client.invalidateQueries({ queryKey: ['cloture'] });
      void client.invalidateQueries({ queryKey: ['discrepancies'] });
    },
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const motifs = useMemo(
    () =>
      [...new Set((recap.data?.lignes ?? []).flatMap((l) => l.parMotif.map((m) => m.motif)))].sort(
        (a, b) => (a === 'AUTRE' ? 1 : b === 'AUTRE' ? -1 : a.localeCompare(b)),
      ),
    [recap.data],
  );

  return (
    <>
      <h1>Clôture de fin d&apos;année</h1>

      <p className="bandeau-avertissement" role="note">
        <strong>Rien n&apos;est transformé automatiquement</strong>
        <span>
          Le récapitulatif liste ce qui a été <strong>expliqué sans être résorbé</strong> : c&apos;est
          la base des ODE annuels. Une fois les ODE passés par KN et réimportés, l&apos;outil les
          rapproche des temporaires et <strong>propose</strong> la bascule en définitif — la
          confirmation reste humaine, et tout est audité.
        </span>
      </p>

      <div className="carte">
        <div className="barre-actions">
          <label className="champ" htmlFor="cloture-du">
            <span>Du</span>
            <input
              id="cloture-du"
              type="date"
              value={du}
              onChange={(event) => setDu(event.target.value)}
            />
          </label>
          <label className="champ" htmlFor="cloture-au">
            <span>Au</span>
            <input
              id="cloture-au"
              type="date"
              value={au}
              onChange={(event) => setAu(event.target.value)}
            />
          </label>
          <label className="champ" htmlFor="cloture-maille">
            <span>Maille</span>
            <select
              id="cloture-maille"
              value={maille}
              onChange={(event) => setMaille(event.target.value as DiscrepancyScope)}
            >
              <option value="ARTICLE">Article</option>
              <option value="FAMILLE">Famille d&apos;équivalence</option>
            </select>
          </label>
        </div>
      </div>

      {erreur && <p className="alerte alerte--erreur">{erreur}</p>}

      <section className="carte">
        <div className="barre-actions barre-actions--titre">
          <h2>Récapitulatif des validations temporaires</h2>
          <div className="barre-actions__boutons">
            <a
              className="bouton bouton--secondaire"
              href={api.recapitulatifExportUrl(filtres)}
              aria-disabled={(recap.data?.lignes.length ?? 0) === 0}
            >
              Exporter en Excel
            </a>
          </div>
        </div>

        {recap.isPending && <p className="chargement">Chargement du récapitulatif…</p>}
        {recap.isError && <p className="alerte alerte--erreur">{messageErreur(recap.error)}</p>}

        {recap.data && recap.data.lignes.length === 0 && (
          <p className="alerte alerte--info" role="status">
            Aucune validation temporaire active sur cette période et cette maille. Rien à déclarer
            en ODE annuel.
          </p>
        )}

        {recap.data && recap.data.lignes.length > 0 && (
          <>
            <div className="totaux">
              <div>
                <span>Clés concernées</span>
                <strong>{recap.data.totaux.cles.toLocaleString('fr-FR')}</strong>
              </div>
              <div>
                <span>Validations reprises</span>
                <strong>{recap.data.totaux.validations.toLocaleString('fr-FR')}</strong>
              </div>
              <div>
                <span>Total à déclarer</span>
                <strong className="nombre">{formaterNombre(recap.data.totaux.quantite)} UVC</strong>
              </div>
            </div>

            <div className="tableau-defilant">
              <table className="tableau">
                <thead>
                  <tr>
                    <th scope="col">Clé</th>
                    <th scope="col">Libellé</th>
                    <th scope="col">Validations</th>
                    {motifs.map((motif) => (
                      <th key={motif} scope="col">
                        {motif}
                      </th>
                    ))}
                    <th scope="col">Total (UVC)</th>
                  </tr>
                </thead>
                <tbody>
                  {recap.data.lignes.map((ligne) => (
                    <tr key={ligne.discrepancyId}>
                      <td>
                        {ligne.maille === 'FAMILLE' ? ligne.codpros.join(' / ') : ligne.cle}
                      </td>
                      <td>{ligne.libelle}</td>
                      <td className="droite">{ligne.validations}</td>
                      {motifs.map((motif) => (
                        <td key={motif} className="droite">
                          {formaterNombre(
                            ligne.parMotif.find((m) => m.motif === motif)?.quantite ?? '0',
                          )}
                        </td>
                      ))}
                      <td className="droite">
                        <strong>{formaterNombre(ligne.total)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <section className="carte">
        <h2>Re-match des ODE vers une validation définitive</h2>
        <p className="sous-titre">
          Un ODE enregistre côté douane ce que le WMS portait déjà : son net douane vaut exactement
          la quantité validée. La bascule n&apos;est proposée que lorsque l&apos;écart est
          effectivement revenu à zéro — une validation définitive <em>constate</em> une
          régularisation, elle ne l&apos;anticipe pas.
        </p>

        {rematch.isPending && <p className="chargement">Rapprochement des ODE…</p>}
        {rematch.isError && <p className="alerte alerte--erreur">{messageErreur(rematch.error)}</p>}

        {rematch.data && rematch.data.length === 0 && (
          <p className="alerte alerte--info" role="status">
            Aucune validation temporaire à rapprocher sur cette période.
          </p>
        )}

        {rematch.data && rematch.data.length > 0 && (
          <table className="tableau">
            <thead>
              <tr>
                <th scope="col">Clé</th>
                <th scope="col">Validé temporairement</th>
                <th scope="col">ODE rapprochés</th>
                <th scope="col">Écart courant</th>
                <th scope="col">Couverture</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {rematch.data.map((proposition) => (
                <tr key={proposition.discrepancyId}>
                  <td>
                    {proposition.cle}
                    <div className="sous-titre">{proposition.libelle}</div>
                  </td>
                  <td className="droite">{formaterNombre(proposition.quantiteValidee)}</td>
                  <td className="droite">
                    {formaterNombre(proposition.quantiteOde)}
                    {proposition.odes.length > 0 && (
                      <div className="sous-titre">
                        {proposition.odes.map((o) => o.numcmm ?? o.jour).join(', ')}
                      </div>
                    )}
                  </td>
                  <td className="droite">{formaterNombre(proposition.ecartUvc)}</td>
                  <td>
                    <span
                      className={
                        proposition.couverture === 'EXACTE'
                          ? 'etiquette etiquette--actif'
                          : 'etiquette etiquette--attente'
                      }
                    >
                      {COUVERTURE_REMATCH_LABELS[proposition.couverture]}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="bouton"
                      disabled={
                        !peutValider || !proposition.validableMaintenant || basculer.isPending
                      }
                      onClick={() => basculer.mutate(proposition)}
                    >
                      {libelleAction(proposition)}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="carte">
        <div className="barre-actions barre-actions--titre">
          <h2>Historique des validations</h2>
          <div className="barre-actions__boutons">
            <a className="bouton bouton--secondaire" href={api.historiqueExportUrl({ du, au })}>
              Exporter en Excel
            </a>
          </div>
        </div>
        <p className="sous-titre">
          Rien n&apos;est supprimé : une validation annulée reste tracée, avec son auteur et son
          motif d&apos;annulation.
        </p>

        {historique.isPending && <p className="chargement">Chargement de l&apos;historique…</p>}
        {historique.isError && (
          <p className="alerte alerte--erreur">{messageErreur(historique.error)}</p>
        )}

        {historique.data && historique.data.length === 0 && (
          <p className="alerte alerte--info" role="status">
            Aucune validation posée sur cette période.
          </p>
        )}

        {historique.data && historique.data.length > 0 && (
          <div className="tableau-defilant">
            <table className="tableau">
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Clé</th>
                  <th scope="col">Maille</th>
                  <th scope="col">Type</th>
                  <th scope="col">Quantité</th>
                  <th scope="col">Commentaire</th>
                  <th scope="col">Utilisateur</th>
                  <th scope="col">Statut</th>
                </tr>
              </thead>
              <tbody>
                {historique.data.map((validation) => (
                  <tr key={validation.id}>
                    <td>{formaterHorodatage(validation.createdAt)}</td>
                    <td>{validation.cle}</td>
                    <td>{DISCREPANCY_SCOPE_LABELS[validation.maille]}</td>
                    <td>{VALIDATION_TYPE_LABELS[validation.type]}</td>
                    <td className="droite">{formaterNombre(validation.quantite)}</td>
                    <td>{validation.commentaire}</td>
                    <td>{validation.userEmail ?? '—'}</td>
                    <td>
                      <span
                        className={
                          validation.statut === 'ACTIVE'
                            ? 'etiquette etiquette--actif'
                            : 'etiquette'
                        }
                      >
                        {validation.statut === 'ACTIVE' ? 'Active' : 'Annulée'}
                      </span>
                      {validation.motifAnnulation && (
                        <div className="sous-titre">{validation.motifAnnulation}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
